import { Component, OnInit, ViewChild, ElementRef, NgZone, Output, EventEmitter, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TechnicalServiceFilter, Coords, SearchResult, ElementFix, TechnicalService } from '../../interfaces';
import { MapsAPILoader } from '@agm/core';
import { MapService, GeolocationService, RepairItemService, TechnicalServiceService } from 'src/app/services';
import { SearchPanelComponent } from './search-panel.component';
import { PlaceService } from 'src/app/services/place.service';

/**
 * Variable to use the Google API
 */
declare const google: any;
/**
 * Search panel to filter the technical services
 *
 * @export
 * @class SearchRepairItemPanelComponent
 * @implements {OnInit}
 */
@Component({
  selector: 'app-search-repair-item-panel',
  templateUrl: './search-repair-item-panel.component.html',
  styleUrls: ['./search-panel.component.scss'],
})
export class SearchRepairItemPanelComponent extends SearchPanelComponent implements OnInit {
  /**
   * Search input reference
   *
   * @type {ElementRef}
   * @memberof SearchRepairItemPanelComponent
   */
  @ViewChild('addressSearch', {
    read: false,
    static: true,
  })
  addressSearchRef: ElementRef;

  /**
   * Emit an event with all the results
   *
   * @type {EventEmitter<SearchResult[]>}
   * @memberof SearchRepairItemPanelComponent
   */
  @Output() searchResult: EventEmitter<SearchResult> = new EventEmitter();

  /**
   * Search Form
   *
   * @type {FormGroup}
   * @memberof SearchRepairItemPanelComponent
   */
  searchForm: FormGroup;

  /**
   * Main items to repair (SubCategories)
   *
   * @type {ElementFix[]}
   * @memberof SearchRepairItemPanelComponent
   */
  elememts: ElementFix[] = [];

  /**
   * Selected Subcategory from URL query
   *
   * @type {ElementFix}
   * @memberof SearchRepairItemPanelComponent
   */
  @Input() selectedElement: ElementFix;

  /**
   * Flag to show a loading indicator when the user search for locations
   *
   * @type {boolean}
   * @memberof SearchRepairItemPanelComponent
   */
  isSearching: boolean = false;

  /**
   * Flag to show a loading indicator when the browser gets the current geolocation
   *
   * @type {boolean}
   * @memberof SearchRepairItemPanelComponent
   */
  isGeolocating: boolean = false;

  /**
   * Flag to show inputs of categories to search
   *
   * @type {boolean}
   * @memberof SearchRepairItemPanelComponent
   */
  isSearchCategories: boolean = false;

  /**
   * Location from URL query
   *
   * @type {String}
   * @memberof SearchRepairItemPanelComponent
   */
  location: String;

  /**
   * Creates an instance of SearchPanelComponent.
   * @param {FormBuilder} fb
   * @param {RepairItemService} repairItemService
   * @param {NgZone} ngZone
   * @param {MapsAPILoader} mapApiLoader
   * @param {MapService} mapService
   * @param {GeolocationService} geolocationService
   * @param {TechnicalServiceService} technicalServiceService
   * @memberof SearchRepairItemPanelComponent
   */
  constructor(
    protected fb: FormBuilder,
    private repairItemService: RepairItemService,
    protected ngZone: NgZone,
    protected mapApiLoader: MapsAPILoader,
    protected mapService: MapService,
    protected geolocationService: GeolocationService,
    protected technicalServiceService: TechnicalServiceService,
    protected placeService: PlaceService,
    private route: ActivatedRoute,
  ) {
    super(fb, ngZone, mapApiLoader, mapService, geolocationService, technicalServiceService, placeService);
  }

  /**
   * - Bind Google places input
   * - Load main categories
   *
   * @memberof SearchRepairItemPanelComponent
   */
  ngOnInit() {
    this.createForm();
    this.initAutocomplete();
    this.loadRepairItems();
  }

  setSearchFilter(form: FormGroup): TechnicalServiceFilter {
    const searchFilter: TechnicalServiceFilter = {
      elementFix: form.value.elementFix,
      coords: form.value.coords,
      postalCode:''
    };
    this.notifyDirectChange(searchFilter);
    return searchFilter;
  }

  /**
   * If the category selected includes direct change it's show on the screen
   *
   * @private
   * @param {TechnicalServiceFilter} searchFilter
   * @memberof SearchRepairItemPanelComponent
   */
  private notifyDirectChange(searchFilter: TechnicalServiceFilter) {
    const elementFix = searchFilter.elementFix;

    if (!!elementFix) {
      this.repairItemService.notifyDirectChange(elementFix.element);
    } else {
      this.repairItemService.notifyDirectChange(null);
    }
  }

  /**
   * Load main categories
   *
   * @private
   * @memberof SearchRepairItemPanelComponent
   */
  private async loadRepairItems() {
    this.elememts = await this.repairItemService.getElementsFix();
    const elementToRepair = this.route.snapshot.queryParams['elemento_que_repara'];
    if (elementToRepair) {
      this.selectedElement = this.elememts.filter((element) => element.element === elementToRepair)[0];
      this.search(this.searchForm);
    }
  }

  /**
   * Init form with specific validations
   *
   * @private
   * @memberof SearchRepairItemPanelComponent
   */
  protected createForm() {
    this.searchForm = this.fb.group({
      elementFix: [null, [Validators.required]],
      location: [''],
      coords: ['', [Validators.required]],
    });

    // If the user change the location the coords are nulled
    this.searchForm.get('location').valueChanges.subscribe((val) => !val && this.setCoordsOnSearchForm(null));
  }

  protected async searchTechnicalServicesAndLocateInMap(searchFilter: TechnicalServiceFilter): Promise<any> {
    const technicalServices: TechnicalService[] = await this.technicalServiceService.searchRapairElements(searchFilter);

    this.updateData(technicalServices, searchFilter.coords);
    this.emitEvent(technicalServices, searchFilter);
  }

  /**
   * Bind input with Places API
   *
   * @private
   * @memberof SearchPanelComponent
   */
  protected async initAutocomplete() {
    super.initAutocomplete();
    this.findLocationQuery();
  }

  /**
   * Init form with specific validations
   *
   * @private
   * @memberof SearchRepairItemPanelComponent
   */
  private findLocationQuery() {
    this.location = this.route.snapshot.queryParams['location'];
    this.mapService.isMapReady.subscribe((isReady) => {
      if (isReady && this.location) {
        const map = this.mapService.map;

        const request = {
          query: this.location,
          fields: ['name', 'geometry'],
        };

        const service = new google.maps.places.PlacesService(map);
        const component = this;
        service.findPlaceFromQuery(request, function(results, status) {
          if (status === google.maps.places.PlacesServiceStatus.OK) {
            const place: any = results[0];
            // verify result
            if (place.geometry === undefined || place.geometry === null) {
              return;
            }
            const coords: Coords = { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() };
            component.setCoordsOnSearchForm(coords);
            component.search(component.searchForm);
          }
        });
      }
    });
  }
}
